import {
    Component, ElementRef, HostListener, OnInit, ViewChild
} from '@angular/core';
import {Store} from '@ngrx/store';
import {filter, first, map, tap} from 'rxjs/operators';
import {VideoService} from '../shared/services/video.service';
import {FullScreenStateEnum} from '../../enums/full-screen-state.enum';
import {
    BeginSelectNextVideoSceneAction,
    ErrorVideoAction,
    SetIsCurrentSceneLoaded,
    SetStartSceneTimeAction,
    SetTotalTimeAction,
    SetVideoStateTypeAction,
    UpdateVideoMasterVolumeAction,
    VideoEndedAction
} from '../shared/stores/root-store/actions/video.action';
import {VideoStateEnum} from '../../enums/video-state.enum';
import {
    selectActionsFromState,
    selectActualVideoState,
    selectAudioTracksFromState,
    selectCurrentSceneClipFromState,
    selectCurrentSceneFromState,
    selectCurrentTimeFromState,
    selectFullScreenStateFromState,
    selectIsCurrentSceneLoaded,
    selectMasterVideoVolume,
    selectNavigateToTimeFromState,
    selectPreviewMode,
    selectSpaceKeyListenerFromState,
    selectStartSceneTime,
    selectTotalTimeFromState,
    selectUserResolutionFromState,
    selectUserVolumeFromState,
    selectVideoClipResolutions,
    selectVideoClipsFromState,
    selectVideoScenesData
} from '../shared/stores/root-store/selectors/video.selector';
import {selectGenerationStartedTime} from '../shared/stores/root-store/selectors/project.selector';
import VideoState from '../shared/stores/root-store/video.state';
import VideoClipModel from '../../models/video-clip.model';
import {NavigationService} from '../shared/services/navigation.service';
import {combineLatest} from 'rxjs';
import {AutoResolutionService} from '../shared/services/auto-resolution.service';
import {
    AddResolutionToNotAvailableTable,
    VideoUserResolutionUpdateAction
} from '../shared/stores/root-store/actions/timeline.action';
import {EventEnum} from '../../enums/event.enum';
import {EventTrackingService} from '../shared/services/event-tracking.service';
import {AudioService} from '../shared/services/audio.service';
import {ActionService} from '../shared/services/action.service';
import {ErrorEnum} from '../../enums/error.enum';
import {BrowserService} from '../shared/services/browser.service';
import {SceneMonitoringService} from '../shared/services/scene-monitoring.service';
import SceneStatisticsModel from '../../models/scene-statistics.model';
import TimeModel from '../../models/time.model';
import {MathService} from '../shared/services/math.service';
import {EventEmitterService} from '../shared/services/event-emitter.service';
import {PlayerEventApiEnum} from '../../enums/playerEventApi.enum';

@Component({
    selector: 'eocm-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss']
})
export class VideoComponent implements OnInit {
    @ViewChild('videoPlayer', {
        read: ElementRef
    }) videoPlayer: ElementRef;

    videoList: VideoClipModel[] = [];
    currentVideo: VideoClipModel;
    isFullScreenMode = false;
    videoState: VideoStateEnum;
    sceneTime = 0;
    currentTime = 0;
    endedVideo = false;
    videoEventTarget = null;
    resolution: string;
    resumePlayback = false;
    progress = null;
    isLoading = true;
    cachedTime = null;
    isVideoPaused = true;
    isVideoFirstTimePlayed = false;
    isSpaceKeyListening = true;
    volume = 1;
    jumpPerformed = false;
    scenesStatisticsSchema: string;
    timeArray: number[] = [];
    eventEmitClipsMapArray: any[] = [];

    audioContext: AudioContext;
    audioSource: MediaElementAudioSourceNode;
    gainNode: GainNode;

    retryMaxAttempts = 2;
    retryInterval = 2000; // ms
    stuckProtectionTime = 5000; // ms
    measurementError = 1; // s
    sceneProgressStep = 20;
    minTime = 0.01;

    private retryAttemptsMade = 0;
    private descriptorAge: number;
    private videoBufferingStartTimestamp: number;
    private isPreview = false;
    private resolutions = [];
    private timeout: ReturnType<typeof setTimeout>;

    constructor(private store: Store<{ video: VideoState }>,
        private videoService: VideoService,
        private navigateService: NavigationService,
        private autoResolutionService: AutoResolutionService,
        private tracking: EventTrackingService,
        private actionService: ActionService,
        private audio: AudioService,
        private browser: BrowserService,
        private sceneMonitoring: SceneMonitoringService,
        private mathService: MathService,
        private eventEmitterService: EventEmitterService
    ) {
    }

    ngOnInit() {
        const store = this.store;
        combineLatest(
            store.select(selectVideoClipsFromState),
            store.select(selectCurrentSceneFromState),
            store.select(selectStartSceneTime),
            store.select(selectFullScreenStateFromState),
            store.select(selectVideoClipResolutions),
            store.select(selectUserVolumeFromState),
            store.select(selectMasterVideoVolume),
            store.select(selectVideoScenesData)
        ).pipe(tap(([
            videoClips,
            currentScene,
            startTime,
            fullScreenState,
            resolutions,
            volume,
            masterVolume,
            videoScenesData
        ]) => {
            this.videoList = videoClips;
            this.currentVideo = currentScene;
            this.sceneTime = startTime;
            this.volume = (volume / 100) * (masterVolume / 100);

            if (videoScenesData.length > 0 && !this.scenesStatisticsSchema) {
                const defaultScenesStatistics = [];
                let clipOrder = 1;

                for (const clip of videoScenesData) {
                    const sceneStatisticsModel = new SceneStatisticsModel();
                    const timeModel = new TimeModel();

                    timeModel.start = this.mathService.roundTo2DecimalPlaces(clip.time.start);
                    timeModel.end = this.mathService.roundTo2DecimalPlaces(clip.time.end);

                    sceneStatisticsModel.uid = clip.uid;
                    sceneStatisticsModel.time = timeModel;
                    sceneStatisticsModel.totalTimeWatched = [];

                    defaultScenesStatistics.push(sceneStatisticsModel);

                    this.eventEmitClipsMapArray.push({
                        uid: clip.uid,
                        time: timeModel,
                        order: clipOrder,
                        eventEmitted: false
                    });

                    clipOrder++;
                }

                this.scenesStatisticsSchema = JSON.stringify(defaultScenesStatistics);
            }

            if (this.gainNode) {
                this.gainNode.gain.value = Number(this.volume.toFixed(2));
            }

            this.fullScreen(fullScreenState);
            this.resolutions = resolutions;
            if (!resolutions.includes(this.resolution)) {
                this.resolution = this.autoResolutionService.getBestResolution(resolutions);
            }

            if (currentScene && currentScene.resolutions[this.resolution] !== this.currentVideo.url) {
                this.currentVideo.url = currentScene.resolutions[this.resolution];
                store.dispatch(VideoUserResolutionUpdateAction({
                    changedResolution: this.resolution
                }));
            }
        })).subscribe();

        store
            .select(selectActualVideoState)
            .pipe(
                map(state => {
                    this.videoState = state;

                    if (this.videoState === VideoStateEnum.Buffering) {
                        this.videoBufferingStartTimestamp = Date.now();
                    }

                    if (state === VideoStateEnum.Ended) {
                        this.setVideoToReplay();
                    }

                    return state;
                }),
                filter((pauseType: VideoStateEnum) => this.isVideoPause(pauseType)),
                map((stateType: VideoStateEnum) => {
                    this.playOrPauseVideo(stateType);
                })
            ).subscribe();

        store
            .select(selectNavigateToTimeFromState)
            .pipe(
                filter(() => this.ensureVideoEventTarget()),
                map(navigateToTime => this.setCurrentVideoTime(navigateToTime))
            ).subscribe();

        store
            .select(selectUserResolutionFromState)
            .subscribe(resolution => {
                this.resolution = resolution;
                this.setVideoResolution();
            });

        store
            .select(selectGenerationStartedTime)
            .subscribe(generationStartedAt => {
                this.descriptorAge = Date.now() - generationStartedAt.getTime();
            });

        store
            .select(selectCurrentTimeFromState)
            .subscribe(currentTime => this.processSceneStartEmitEvent(currentTime));

        store
            .select(selectCurrentSceneClipFromState)
            .pipe(
                filter(Boolean)
            )
            .subscribe(currentSceneClip => {
                this.store.dispatch(UpdateVideoMasterVolumeAction({
                    currentVolume: currentSceneClip.volume
                }));
            });

        this.setLoadingState();
        this.getPreviewMode();
        this.initializeAudioContext();
        this.handleActionData();
        this.setNavigateOptions();
        this.isVideoStatePause();
        this.isSpaceKeyDisabled();

        window.addEventListener('beforeunload', () => {
            this.sendVideoPlayedSceneStatistics();
        });
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === ' ' && this.isSpaceKeyListening && (this.videoState !== VideoStateEnum.Interaction)) {
            (this.isVideoPaused || this.videoState === VideoStateEnum.Ended)
                ? this.store.dispatch(SetVideoStateTypeAction({
                    videoState: VideoStateEnum.Playing
                }))
                : this.store.dispatch(SetVideoStateTypeAction({
                    videoState: VideoStateEnum.UserPause
                }));
        }
    }

    handleActionData() {
        this.store.select(selectActionsFromState).pipe(filter((actions) => actions.length > 0), first())
            .subscribe(actions => {
                this.actionService.createTimeActionTable(actions);
            });
    }

    loadedDataEvent($event: Event) {
        this.initializeAudio();

        if (this.videoPlayer.nativeElement.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
            return;
        }

        if (!Number.isNaN(this.videoPlayer.nativeElement.duration)) {
            this.store.dispatch(SetTotalTimeAction({
                totalTime: this.videoPlayer.nativeElement.duration
            }));
        }
        this.setCachedTimeAfterResolutionChange();

        this.videoEventTarget = $event.currentTarget;

        if (this.resumePlayback) {
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.Playing
            }));
            this.resumePlayback = false;
        }

        this.store.dispatch(SetIsCurrentSceneLoaded({
            isCurrentSceneLoaded: true
        }));
    }

    progressEvent($event: Event) {
        this.preventBufferingStuck(false);

        if (this.videoPlayer.nativeElement.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
            this.resumePlayback = this.isVideoFirstTimePlayed && [VideoStateEnum.Playing, VideoStateEnum.Buffering].includes(this.videoState);
            this.store.dispatch(SetIsCurrentSceneLoaded({
                isCurrentSceneLoaded: false
            }));
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.Buffering
            }));

            this.preventBufferingStuck();

            return;
        }

        this.store.dispatch(SetIsCurrentSceneLoaded({
            isCurrentSceneLoaded: true
        }));
        if (this.resumePlayback) {
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.Playing
            }));
            this.resumePlayback = false;
        }

        // On some iOS, it looks like the video doesn't get loaded unless the user hits play
        // listening to the progress event instead of loadeddata seems to work fine on iOS Safari
        if (!this.videoEventTarget) {
            this.loadedDataEvent($event);
        }
    }

    preventBufferingStuck(enable = true) {
        if (enable) {
            this.timeout = setTimeout(() => {
                this.store.dispatch(SetIsCurrentSceneLoaded({
                    isCurrentSceneLoaded: true
                }));
                this.store.dispatch(SetVideoStateTypeAction({
                    videoState: VideoStateEnum.Playing
                }));
            }, this.stuckProtectionTime);
        } else if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }

    onSeeked() {
        if (this.videoPlayer.nativeElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
            this.store.dispatch(SetIsCurrentSceneLoaded({
                isCurrentSceneLoaded: true
            }));
        }
    }

    onError($event: ErrorEvent) {
        const parent = ($event.target as HTMLElement).parentNode as HTMLVideoElement;

        if (parent.networkState === parent.NETWORK_NO_SOURCE) {
            this.retryAttemptsMade++ < this.retryMaxAttempts
                ? this.retryToGetVideo()
                : this.changeToLowerResolution();
        } else {
            this.showNotFoundError();
        }
    }

    private setLoadingState() {
        combineLatest([
            this.store.select(selectIsCurrentSceneLoaded),
            this.store.select(selectAudioTracksFromState),
            this.store.select(selectTotalTimeFromState)
        ]).subscribe(([isCurrentSceneLoaded, audioTracks, totalTime]) => {
            if (isCurrentSceneLoaded && this.audio.isLoaded(audioTracks) && this.isLoading && !isNaN(totalTime)) {
                this.store.dispatch(ErrorVideoAction({
                    errorType: null, error: null
                }));

                const eventData = {
                    video: this.currentVideo.url,
                    timeToFirstFrame: (Date.now() - this.videoBufferingStartTimestamp) / 1000,
                    duration: totalTime,
                    resolutionChanged: this.cachedTime !== null
                };

                this.tracking.track(EventEnum.Loaded, eventData);
                this.eventEmitterService.emitEvent(PlayerEventApiEnum.PlayerInit, eventData);

                this.isLoading = false;
            }
        });
    }

    private changeToLowerResolution() {
        let currentResolutionIndex = this.resolutions.findIndex(resolution => resolution === this.resolution);
        const lowerResolution = this.resolution !== '360p'
            ? this.resolutions[++currentResolutionIndex]
            : null;

        if (lowerResolution && !this.isPreview) {
            this.store.dispatch(ErrorVideoAction({
                errorType: ErrorEnum.RESOLUTION_GENERATING_ERROR,
                error: {
                    resolution: this.resolution
                }
            }));
            this.store.dispatch(AddResolutionToNotAvailableTable({
                resolution: this.resolution
            }));
            this.store.dispatch(VideoUserResolutionUpdateAction({
                changedResolution: lowerResolution
            }));
            this.showGeneratingError();

            this.retryAttemptsMade = 0;
        } else {
            this.showNotFoundError();
        }
    }

    private getPreviewMode() {
        this.store.select(selectPreviewMode)
            .subscribe(isPreview => {
                this.isPreview = isPreview;
                if (isPreview) {
                    this.retryMaxAttempts = 30;
                }
            });
    }

    private retryToGetVideo() {
        this.showGeneratingError();

        setTimeout(() => this.loadVideo(this.currentVideo.url), this.retryInterval);
    }

    private showGeneratingError() {
        this.store.dispatch(ErrorVideoAction({
            errorType: ErrorEnum.GENERATING,
            error: {
                message: `Generating - retry attempt ${this.retryAttemptsMade} of ${this.retryMaxAttempts}`
            }
        }));
    }

    private showNotFoundError() {
        this.store.dispatch(ErrorVideoAction({
            errorType: ErrorEnum.VIDEO_NOT_FOUND,
            error: {
                message: 'Video not found'
            }
        }));
    }

    private ensureVideoEventTarget(): boolean {
        const videoElement = this.videoPlayer?.nativeElement;

        if (!this.videoEventTarget && videoElement && videoElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
            this.videoEventTarget = videoElement;
        }

        return !!this.videoEventTarget;
    }

    setCurrentVideoTime(newTime) {
        this.store.dispatch(SetIsCurrentSceneLoaded({
            isCurrentSceneLoaded: false
        }));
        this.store.dispatch(SetVideoStateTypeAction({
            videoState: VideoStateEnum.Buffering
        }));
        this.jumpPerformed = true;

        this.videoEventTarget.currentTime = this.currentVideo.duration > newTime
            ? newTime
            : this.currentVideo.duration;

        this.resetClipsForEmitEvent();
        this.updateTime();
    }

    updateTime() {
        this.currentTime = this.videoPlayer.nativeElement.currentTime;

        this.timeArray.push(this.currentTime);

        if (this.videoPlayer.nativeElement.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
            this.preventBufferingStuck(false);
        }

        this.trackProgress();
    }

    ended() {
        this.endedVideo = !this.videoList[this.videoService.currentIndex(this.videoList, this.currentVideo.id) + 1];

        if (this.endedVideo) {
            this.store.dispatch(VideoEndedAction());
        } else {
            this.prepareNextSceneToPlay();
            this.videoPlayer.nativeElement.play();
        }
    }

    private setCachedTimeAfterResolutionChange() {
        if (this.cachedTime) {
            this.videoPlayer.nativeElement.currentTime = this.cachedTime;
            this.cachedTime = null;
        }
    }

    private setVideoResolution() {
        if (this.currentVideo && this.videoPlayer) {
            this.cachedTime = this.videoPlayer.nativeElement.currentTime;

            this.loadVideo(this.currentVideo.resolutions[this.resolution]);
            this.resumePlayback = this.videoState === VideoStateEnum.Playing;

            if (this.resumePlayback) {
                this.store.dispatch(SetVideoStateTypeAction({
                    videoState: VideoStateEnum.Buffering
                }));
            }

            if (!this.endedVideo && this.videoEventTarget) {
                this.videoEventTarget.currentTime = this.cachedTime;
                this.updateTime();
            }
        }
    }

    private loadVideo(videoUrl: string) {
        if (this.videoPlayer && videoUrl) {
            this.store.dispatch(SetIsCurrentSceneLoaded({
                isCurrentSceneLoaded: false
            }));
            this.isLoading = true;
            this.currentVideo.url = videoUrl;
            this.videoPlayer.nativeElement.load();

            this.tracking.track(EventEnum.Loading, {
                video: videoUrl, attempt: this.retryAttemptsMade
            });
        }
    }

    private fullScreen(fullScreenState: FullScreenStateEnum) {
        this.isFullScreenMode = fullScreenState === FullScreenStateEnum.Active;
    }

    private isVideoPause(type) {
        return [
            VideoStateEnum.Playing,
            VideoStateEnum.UserPause,
            VideoStateEnum.Buffering,
            VideoStateEnum.Interaction
        ].includes(type);
    }

    private isVideoStatePause() {
        this.store.select(selectActualVideoState).subscribe((currentState) => {
            this.isVideoPaused = [
                VideoStateEnum.UserPause,
                VideoStateEnum.Pause
            ].includes(currentState);
        });
    }

    private playOrPauseVideo(pauseType) {
        if (this.videoPlayer) {
            if (pauseType === VideoStateEnum.Playing) {
                this.videoPlayer.nativeElement
                    .play()
                    .catch((reason) => {
                        if (reason.name === 'NotAllowedError' || (reason.name === 'AbortError' && this.browser.isSafari())) {
                            this.store.dispatch(SetVideoStateTypeAction({
                                videoState: VideoStateEnum.Pause
                            }));
                        }
                    });
            } else {
                this.videoPlayer.nativeElement.pause();
            }

            if (pauseType === VideoStateEnum.Playing && this.isVideoFirstTimePlayed === false) {
                this.isVideoFirstTimePlayed = true;
                this.tracking.track(EventEnum.FirstPlay, {
                    videoPlayStartTime: (Date.now() - this.videoBufferingStartTimestamp) / 1000
                });
            }
        }
    }

    private setNavigateOptions() {
        this.sceneTime = 0;
        this.navigateService.setTimeToStartScene(this.videoState).pipe(
            map(time => {
                this.store.dispatch(SetStartSceneTimeAction({
                    startSceneTime: time
                }));
            })).subscribe();
    }

    private prepareNextSceneToPlay() {
        this.store.dispatch(BeginSelectNextVideoSceneAction({
            payload: {
                currentSceneId: this.currentVideo.id
            }
        }));
    }

    private setVideoToReplay() {
        this.videoState = VideoStateEnum.Ended;
        this.progress = null;
        this.currentVideo = this.videoList[0];

        window.parent.postMessage('ended', '*');
    }

    private trackProgress() {
        const length = this.timeArray.length;
        const lastIndex = length - 1;
        const videoDuration = this.videoPlayer.nativeElement.duration;
        const isCompleted = this.timeArray[lastIndex] > videoDuration - this.measurementError;
        const progress = Math.round(100 * this.timeArray[lastIndex] / this.videoPlayer.nativeElement.duration);

        if (this.progress < progress) {
            this.progress = progress;
        }

        if(isCompleted) {
            if (this.timeArray[lastIndex] < videoDuration) {
                this.timeArray.push(videoDuration); // Fill the array to obtain a full timespan for the end scene
            }
            this.sendVideoCompletedSceneStatistics();

            return;
        }

        if(length >= this.sceneProgressStep) {
            this.sendVideoPlayedSceneStatistics();

            return;
        }
    }

    private isSpaceKeyDisabled() {
        this.store.select(selectSpaceKeyListenerFromState).subscribe((isSpaceKeyListenerOn) => {
            this.isSpaceKeyListening = isSpaceKeyListenerOn;
        });
    }

    private initializeAudio() {
        if (!this.audioSource) {
            this.audioSource = this.audioContext.createMediaElementSource(this.videoPlayer.nativeElement);
            this.gainNode = this.audioContext.createGain();

            this.audioSource.connect(this.gainNode);
            this.gainNode.connect(this.audioContext.destination);

            this.gainNode.gain.value = Number(this.volume.toFixed(2));
        }
    }

    private initializeAudioContext() {
        this.audioContext = this.audio.getAudioContext();
    }

    private sendVideoCompletedSceneStatistics() {
        const videoDuration = this.videoPlayer.nativeElement.duration;
        const statistics = this.sceneMonitoring
            .monitor(this.timeArray, JSON.parse(this.scenesStatisticsSchema), this.measurementError, this.minTime);

        if (JSON.stringify(statistics) !== this.scenesStatisticsSchema) { // Prevent from sending empty statistics
            const eventData = {
                statistics,
                progress: 100
            };

            this.tracking.track(EventEnum.VideoCompleted, eventData);
            this.eventEmitterService.emitEvent(PlayerEventApiEnum.VideoCompleted, eventData)
        }

        this.timeArray = [videoDuration - 0.01]; // Get the last element to keep continuous measurement
    }

    private sendVideoPlayedSceneStatistics() {
        const length = this.timeArray.length;
        const lastIndex = length - 1;
        const statistics = this.sceneMonitoring
            .monitor(this.timeArray, JSON.parse(this.scenesStatisticsSchema), this.measurementError, this.minTime);

        if (JSON.stringify(statistics) !== this.scenesStatisticsSchema) { // Prevent from sending empty statistics
            const eventData = {
                statistics,
                progress: this.progress
            };

            this.tracking.track(EventEnum.VideoPlayed, eventData);
        }

        this.timeArray = [this.timeArray[lastIndex]]; // Get the last element to keep continuous measurement

    }

    private resetClipsForEmitEvent() {
        for(const clip of this.eventEmitClipsMapArray) {
            clip.eventEmitted = false;
        }
    }

    private processSceneStartEmitEvent(currentTime) {
        if (!this.isLoading) {
            const currentClipIndex = this
                .eventEmitClipsMapArray
                .findIndex(videoClip => currentTime < videoClip.time.end && currentTime >= videoClip.time.start)
            ;

            if (currentClipIndex >= 0 && !this.eventEmitClipsMapArray[currentClipIndex].eventEmitted) {
                this.eventEmitterService
                    .emitEvent(PlayerEventApiEnum.SceneStart, {
                        clip: this.eventEmitClipsMapArray[currentClipIndex],
                        currentTime
                    })
                ;

                this.eventEmitClipsMapArray[currentClipIndex].eventEmitted = true;
            }
        }
    }
}
